import { useState, useCallback } from 'react'
import { apiClient } from '@/services/api/client'
import { buildTourSystemPrompt } from '../lib/tourSystemPrompt'

const hintCache = new Map<string, string>()

const FALLBACK_HINTS: Record<string, string> = {
  'What is the Vharta Platform and what can I do with it?':
    'Vharta is a multi-tenant workflow orchestration platform. You can run Dagster assets, Airflow DAGs, Langflow pipelines, and MCP tool calls with built-in governance, cost attribution, and audit logging.',
  'How is the sidebar navigation organized and how do I find specific features?':
    'The sidebar is grouped into Home, Build, Operate, AI, Infrastructure, and Administration sections. Use the search bar (Ctrl+K) for fast navigation to any feature.',
  'What do the different execution status categories mean and how do I act on them?':
    'Pending means waiting for a worker slot. Running means actively executing. Failed means the workflow raised an error — click the row to see the error log.',
  'What kinds of notifications does Vharta send and how do I configure them?':
    'Vharta sends workflow failure alerts, cluster health changes, and budget threshold events. Go to Integrations > Notification Channels to connect Slack, PagerDuty, or email.',
  'What is a tenant in Vharta and how does multi-tenancy affect my resources?':
    'A tenant is an isolated workspace. All workflows, clusters, users, and billing are scoped to your tenant. You cannot see resources from other tenants.',
}

interface AIHintResult {
  hint: string | null
  loading: boolean
  error: string | null
  fetchHint: (context: string, role: string, stepTitle?: string) => void
}

export function useAIHint(): AIHintResult {
  const [hint, setHint] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchHint = useCallback(async (context: string, role: string, stepTitle?: string) => {
    const cacheKey = `${stepTitle ?? ''}::${role}::${context.slice(0, 120)}`

    if (hintCache.has(cacheKey)) {
      setHint(hintCache.get(cacheKey)!)
      return
    }

    if (FALLBACK_HINTS[context]) {
      const fallback = FALLBACK_HINTS[context]
      hintCache.set(cacheKey, fallback)
      setHint(fallback)
      return
    }

    setLoading(true)
    setError(null)

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await apiClient.post<{ text?: string; content?: string }>(
        '/tenant/ai/completion',
        {
          prompt: context,
          max_tokens: 200,
          system: buildTourSystemPrompt(role),
        },
      )

      const text = response?.text ?? response?.content ?? ''
      hintCache.set(cacheKey, text)
      setHint(text)
    } catch {
      const fallback = FALLBACK_HINTS[context]
      if (fallback) {
        hintCache.set(cacheKey, fallback)
        setHint(fallback)
      } else {
        setError('Unable to load AI hint. Please refer to the documentation.')
      }
    } finally {
      clearTimeout(timeout)
      setLoading(false)
    }
  }, [])

  return { hint, loading, error, fetchHint }
}
